import { useEffect } from 'react'
import mapboxgl from 'mapbox-gl'
import {
  getAllEmploymentLayerIds,
  createEmploymentFillColorExpression,
  EMPLOYMENT_LAYER_ORDER,
  getEmploymentField,
  formatEmploymentValue,
  getEmploymentLayerDisplayName,
} from '../../../../molecules/AnalysisMap/utils/employmentLayerHelpers'
import { getLayerConfig } from '../../../../molecules/AnalysisMap/config'
import { API_BASE_URL } from '../../../../../config'

interface UseEmploymentLayersProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  popup: React.MutableRefObject<mapboxgl.Popup | null>
  styleLoadedCount: number
  otherMapOptions: any
}

/**
 * Hook to handle employment choropleth layers and hover tooltips
 */
export const useEmploymentLayers = ({
  map,
  mapLoaded,
  popup,
  styleLoadedCount,
  otherMapOptions,
}: UseEmploymentLayersProps) => {
  // Handle employment choropleth layers (vector tiles)
  useEffect(() => {
    if (!map.current || !mapLoaded) {
      return
    }

    // This prop will be passed from AnalysisMap when employment section is active
    const employmentSection =
      (otherMapOptions as any).activeSection === 'employment'
    const employmentLayerStates = (otherMapOptions as any).employmentLayerStates

    if (!employmentSection || !employmentLayerStates) {
      // Remove employment source and layers if not in employment section
      let existingEmploymentLayers: string[] = []
      try {
        const style = map.current.getStyle()
        if (style?.layers) {
          existingEmploymentLayers = style.layers
            .filter((layer) => layer.id.startsWith('employment-'))
            .map((layer) => layer.id)
        }
      } catch {
        // Style not loaded yet, skip cleanup
        return
      }

      existingEmploymentLayers.forEach((layerId) => {
        if (map.current!.getLayer(layerId)) {
          try {
            map.current!.removeLayer(layerId)
          } catch (error) {
            console.warn(`Failed to remove employment layer ${layerId}:`, error)
          }
        }
      })

      if (map.current!.getSource('employment-vector-tiles')) {
        try {
          map.current!.removeSource('employment-vector-tiles')
        } catch (error) {
          console.warn('Failed to remove employment source:', error)
        }
      }

      return
    }

    // Employment section is active - add vector tile source and layers
    // Add vector tile source if it doesn't exist
    if (!map.current!.getSource('employment-vector-tiles')) {
      map.current!.addSource('employment-vector-tiles', {
        type: 'vector',
        tiles: [`${API_BASE_URL}/employment/vector-tiles/{z}/{x}/{y}`],
        minzoom: 10,
        maxzoom: 16,
      })
    }

    // Add or update fill layers for each employment layer
    const layerIds = getAllEmploymentLayerIds()

    layerIds.forEach((layerId: string) => {
      const layerConfig = getLayerConfig(layerId)
      const layerState = employmentLayerStates[layerId] || {
        enabled: layerConfig?.defaultEnabled || false,
        opacity: layerConfig?.defaultOpacity || 70,
      }

      if (!map.current!.getLayer(layerId)) {
        // Add new fill layer

        const fillColorExpression = createEmploymentFillColorExpression(
          layerId,
          layerConfig
        )

        map.current!.addLayer({
          id: layerId,
          type: 'fill',
          source: 'employment-vector-tiles',
          'source-layer': 'hits', // searchMvt returns polygons in 'hits' layer
          layout: {
            visibility: layerState.enabled ? 'visible' : 'none',
          },
          paint: {
            'fill-outline-color': 'transparent',
            'fill-color': fillColorExpression,
            'fill-opacity': layerState.opacity / 100,
          },
        })
      } else {
        // Update existing layer visibility and opacity
        map.current!.setLayoutProperty(
          layerId,
          'visibility',
          layerState.enabled ? 'visible' : 'none'
        )
        map.current!.setPaintProperty(
          layerId,
          'fill-opacity',
          layerState.opacity / 100
        )
      }
    })

    // Apply Z-ordering to ensure proper layer stacking
    EMPLOYMENT_LAYER_ORDER.forEach((layerId: string, index: number) => {
      if (map.current!.getLayer(layerId) && index > 0) {
        const previousLayerId = EMPLOYMENT_LAYER_ORDER[index - 1]
        if (map.current!.getLayer(previousLayerId)) {
          try {
            map.current!.moveLayer(layerId, previousLayerId)
          } catch {
            // Layer ordering might fail if layers don't exist yet, that's ok
          }
        }
      }
    })
  }, [mapLoaded, styleLoadedCount, otherMapOptions])

  // Setup hover tooltips for employment layers
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    // Get all enabled employment layers
    const enabledEmploymentLayers = getAllEmploymentLayerIds().filter(
      (layerId) => {
        const layerState = otherMapOptions.employmentLayerStates?.[layerId]
        return layerState?.enabled && map.current!.getLayer(layerId)
      }
    )

    if (enabledEmploymentLayers.length === 0) return

    // Find the topmost visible layer (based on Z-order)
    const topmostLayer = EMPLOYMENT_LAYER_ORDER.slice()
      .reverse()
      .find((layerId) => enabledEmploymentLayers.includes(layerId))

    if (!topmostLayer) return

    const field = getEmploymentField(topmostLayer)
    if (!field) return

    // Handle mouse move to show tooltip
    const handleMouseMove = (e: mapboxgl.MapMouseEvent) => {
      const features = map.current!.queryRenderedFeatures(e.point, {
        layers: [topmostLayer],
      })

      if (features.length > 0 && popup.current) {
        const feature = features[0]
        const value = feature.properties?.[field]
        const formattedValue = formatEmploymentValue(topmostLayer, value)
        const layerName = getEmploymentLayerDisplayName(topmostLayer)

        // Change cursor to pointer
        map.current!.getCanvas().style.cursor = 'pointer'

        // Show popup with formatted value
        popup.current
          .setLngLat(e.lngLat)
          .setHTML(
            `<div style="padding: 8px 12px; font-size: 13px;">
              <div style="font-weight: 600; margin-bottom: 4px;">${layerName}</div>
              <div>${formattedValue}</div>
            </div>`
          )
          .addTo(map.current!)
      } else {
        // Reset cursor
        map.current!.getCanvas().style.cursor = ''
        popup.current?.remove()
      }
    }

    // Handle mouse leave to hide tooltip
    const handleMouseLeave = () => {
      map.current!.getCanvas().style.cursor = ''
      popup.current?.remove()
    }

    // Add event listeners
    map.current.on('mousemove', topmostLayer, handleMouseMove)
    map.current.on('mouseleave', topmostLayer, handleMouseLeave)

    // Cleanup
    return () => {
      if (map.current) {
        map.current.off('mousemove', topmostLayer, handleMouseMove)
        map.current.off('mouseleave', topmostLayer, handleMouseLeave)
      }
      popup.current?.remove()
    }
  }, [mapLoaded, otherMapOptions.employmentLayerStates])
}
